import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import validator from 'validator';
import { postRegisterUser, postLoginUser } from '../../actions/auth';
import {
  Button, TextField, Paper, FormControl, InputLabel, Select, MenuItem, InputAdornment
} from '@mui/material';
import eyeShut from '../../img/icons8-eye-50.png';
import eyeOpen from '../../img/icons8-eye-50-2.png';
import backgroundImage from "../../img/LEVLR-Login-Background.jpg";
import levlr_logo from "../../img/LEVLR Logo_CMYK.png";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const RegisterFormInner = () => {
  const [credentials, setCredentials] = useState({});
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [passwordMatchMessage, setPasswordMatchMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [match, setMatch] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isRegistered = useSelector(state => state.isRegistered);
  const isLoggedIn = useSelector(state => state.isLoggedIn);
  const user = useSelector(state => state.user);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  // After registration, auto-login
  useEffect(() => {
    if (isRegistered) handleLoginAfterRegister();
  }, [isRegistered]);

  useEffect(() => {
    if (isLoggedIn) {
      navigate ('/postRegistration')
    }
  }, [isLoggedIn, user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedValue = name === 'email' ? value.toLowerCase().trim() : value.trim();

    if (name === 'password') setPasswordTouched(true);

    if (name === 'phone') {
      setPhoneError(/^\d{10}$/.test(updatedValue) ? '' : 'Please enter a valid 10-digit phone number.');
    }

    setCredentials(prev => ({
      ...prev,
      resetPasswordToken: '',
      resetPasswordExpires: new Date(),
      date_created: new Date(),
      [name]: updatedValue,
    }));

    if (name === 'password' || name === 'confirmPassword') {
      validateConfirm(
        name === 'password' ? updatedValue : credentials.password || '',
        name === 'confirmPassword' ? updatedValue : credentials.confirmPassword || ''
      );
    }
  };

  const handleConfirm = (e) => {
    const confirmValue = e.target.value;
    const passwordValue = credentials.password || '';
    validateConfirm(passwordValue, confirmValue);
    setCredentials({ ...credentials, confirmPassword: confirmValue });
  };

  const validateConfirm = (passwordValue, confirmValue) => {
    if (!passwordValue || !confirmValue) {
      setMatch(false);
      setPasswordMatchMessage('');
      return false;
    }
    if (passwordValue === confirmValue) {
      setMatch(true);
      setPasswordMatchMessage('');
      return true;
    } else {
      setMatch(false);
      setPasswordMatchMessage('Passwords do not match.');
      return false;
    }
  };

  const validatePassword = () => {
    const p = credentials.password || '';
    const errors = [];
    const successes = [];

    if (p.length < 10) errors.push('At least 10 characters'); else successes.push('✓ 10+ characters');
    if (!/[a-z]/.test(p)) errors.push('At least 1 lowercase letter'); else successes.push('✓ lowercase');
    if (!/[A-Z]/.test(p)) errors.push('At least 1 uppercase letter'); else successes.push('✓ uppercase');
    if (!/[!@#$%^&*()+=\-\?;,./{}|":<>\[\]\\' ~_]/.test(p)) errors.push('At least 1 special character'); else successes.push('✓ special character');

    return { errors, successes, valid: errors.length === 0 };
  };

  const validateEmail = () => credentials.email && validator.isEmail(credentials.email);
  const validateUserName = () =>
    credentials.name && credentials.name.length > 0 &&
    credentials.lastName && credentials.lastName.length > 0;

  const allowSignUp = () => {
    const { valid } = validatePassword();
    if (match && valid && phoneError === '' && validateEmail() && validateUserName()) return loginButtonStyle;
    return loginButtonStyleDisabled;
  };

  const registerUser = async (e) => {
    e.preventDefault();
    setErrorMessage('');
  
    if (credentials.website) return; 
  
    const { valid } = validatePassword();
    if (!(match && valid && phoneError === '' && validateEmail() && validateUserName())) return;
  
    if (!executeRecaptcha) {
      setErrorMessage('Recaptcha not yet available');
      return;
    }
  
    try {
      const recaptchaToken = await executeRecaptcha('registerUser');
      await dispatch(postRegisterUser({ credentials, recaptchaToken }));
    } catch {
      setErrorMessage('Registration failed. Please check your details.');
    }
  };
  

  const handleLoginAfterRegister = async () => {
    if (!executeRecaptcha) {
      setErrorMessage('Recaptcha not yet available for login.');
      return;
    }
  
    try {
      // Get recaptcha token for login
      const recaptchaToken = await executeRecaptcha('login');
  
      // Dispatch login with token
      await dispatch(postLoginUser({ credentials, recaptchaToken }));
    } catch (err) {
      console.error("Auto-login failed:", err);
      setErrorMessage('Login after registration failed.');
    }
  };
  

  const checkPassword = () => {
    const { valid } = validatePassword();
    return passwordTouched && (!valid || !(credentials.password || ''));
  };

  const renderPasswordRequirements = () => {
    const { errors, successes } = validatePassword();
    if (!credentials.password) return null;
    return (
      <div style={requirementStyles}>
        {errors.map((msg, i) => <span key={i} style={{ color: '#d32f2f', marginRight: 8 }}>{msg}</span>)}
        {successes.map((msg, i) => <span key={`s-${i}`} style={{ color: '#006d6f', marginRight: 8 }}>{msg}</span>)}
      </div>
    );
  };

  const routeChange = () => navigate('/login');

  // ---------- STYLES ----------
  const backGroundStyle = {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover',
    width: '100vw', height: '100vh', padding: '64px 0'
  };
  const loginPaperStyle = {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.5)', width: 'auto',
    maxWidth: '800px', maxHeight: '90vh', overflow: 'auto',
    paddingTop: '4vh', paddingBottom: '4vh', margin: '32px auto'
  };
  const loginTextFieldStyle = { width: '65%', height: 'auto' };
  const loginFormStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', width: '75%', marginTop: 1 };
  const loginButtonStyle = { width: 'auto', minWidth: '160px', color: "white", backgroundColor: "#2fb7b7", '&:hover': { backgroundColor: '#01656a' }, margin: 16 };
  const loginButtonStyleDisabled = { pointerEvents: 'none', minWidth: '160px', width: 'auto', color: "white", backgroundColor: "#01656a", margin: 16 };
  const returnButtonStyle = { width: 'auto', minWidth: '160px', color: "white", backgroundColor: "#01656a", '&:hover': { backgroundColor: '#2fb7b7' }, margin: 16, fontSize: '70%', whiteSpace: 'nowrap' };
  const levlrLogoStyle = { width: '33%', minWidth: '320px', height: 'auto' };
  const requirementStyles = { padding: 0, margin: 0, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', width: '65%', textAlign: 'left', fontSize: '1.5vh', color: '#d32f2f' };

  return (
    <div style={backGroundStyle}>
      <Paper style={loginPaperStyle}>
        <img src={levlr_logo} style={levlrLogoStyle} alt="Levlr Logo" />
        <form style={loginFormStyle} onSubmit={registerUser}>
        <input
          type="text"
          name="website"
          value={credentials.website || ""}
          onChange={handleChange}
          style={{ display: 'none' }}
          autoComplete="off"
          tabIndex="-1"
        />
          {/* First Name / Last Name / Phone / Email */}
          <TextField variant="outlined" margin="normal" size="small" required id="name" label="First Name" name="name" onChange={handleChange} style={loginTextFieldStyle} error={!credentials.name} helperText={!credentials.name ? 'First Name cannot be blank.' : ' '} InputProps={{ style: { backgroundColor: '#eff0f0', borderRadius: 4 } }} />
          <TextField variant="outlined" margin="normal" size="small" required id="lastName" label="Last Name" name="lastName" onChange={handleChange} style={loginTextFieldStyle} error={!credentials.lastName} helperText={!credentials.lastName ? 'Last Name cannot be blank.' : ' '} InputProps={{ style: { backgroundColor: '#eff0f0', borderRadius: 4 } }} />
          <TextField variant="outlined" margin="normal" size="small" required id="phone" label="Phone" name="phone" type="tel" onChange={handleChange} style={loginTextFieldStyle} error={!!phoneError} helperText={phoneError || ' '} InputProps={{ style: { backgroundColor: '#eff0f0', borderRadius: 4 } }} />
          <TextField variant="outlined" margin="normal" size="small" required id="email" label="Email" name="email" type="email" onChange={handleChange} style={loginTextFieldStyle} error={credentials.email && !validateEmail()} helperText={credentials.email && !validateEmail() ? 'Not a valid email.' : ' '} InputProps={{ style: { backgroundColor: '#eff0f0', borderRadius: 4 } }} />
          {/* Password / Confirm */}
          <TextField variant="outlined" margin="normal" size="small" required id="password" label="Password" name="password" type={showPassword ? 'text' : 'password'} onChange={handleChange} helperText={renderPasswordRequirements()} error={checkPassword()} style={loginTextFieldStyle} InputProps={{ style: { backgroundColor: '#eff0f0', borderRadius: 4 }, endAdornment: (<InputAdornment position="end"><button type="button" onClick={() => setShowPassword(!showPassword)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><img src={showPassword ? eyeShut : eyeOpen} alt="toggle password" style={{ width: 24, height: 24 }} /></button></InputAdornment>) }} />
          <TextField variant="outlined" margin="normal" size="small" required id="confirm" label="Confirm Password" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} onChange={handleConfirm} error={!!passwordMatchMessage} helperText={passwordMatchMessage || ' '} style={loginTextFieldStyle} InputProps={{ style: { backgroundColor: '#eff0f0', borderRadius: 4 }, endAdornment: (<InputAdornment position="end"><button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><img src={showConfirmPassword ? eyeShut : eyeOpen} alt="toggle password" style={{ width: 24, height: 24 }} /></button></InputAdornment>) }} />
          <Button type="submit" variant="contained" style={allowSignUp()}>Sign Up</Button>
          <Button type="button" variant="contained" style={returnButtonStyle} onClick={routeChange}>Return to Login</Button>
          {errorMessage && <div style={{ color: 'red', marginTop: '10px' }}>{errorMessage}</div>}
        </form>
      </Paper>
    </div>
  );
};

const RegisterForm = () => (
  <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}>
    <RegisterFormInner />
  </GoogleReCaptchaProvider>
);

export default RegisterForm;
